import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import {
  AlertTriangle,
  ArrowLeft,
  Boxes,
  CalendarClock,
  ClipboardList,
  Download,
  PackageCheck,
  PackagePlus,
  Plus,
  RefreshCw,
  RotateCcw,
  Save,
  Search,
  ShieldCheck,
  ShoppingCart,
  Trash2,
  X
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { supabaseClient } from '../lib/supabaseClient';

type InventoryUnit = '箱' | '巻' | '個';
type InventoryArea = '介護' | '調理' | '感染症';
type InventoryStatus = 'order' | 'ok' | 'over' | 'unknown';
type InventoryStorageMode = 'local' | 'supabase';

type InventoryItem = {
  id: string;
  sortOrder?: number;
  area: InventoryArea;
  itemName: string;
  size: string;
  unit: InventoryUnit;
  packQuantity: number;
  currentStock: number;
  stockSavedAt: string;
  threeDayUse: number;
  reorderDays: number;
  orderQuantity: number;
};

type EvaluatedInventoryItem = InventoryItem & {
  dailyUse: number;
  elapsedDays: number;
  estimatedStock: number;
  daysLeft: number | null;
  reorderPoint: number | null;
  status: InventoryStatus;
};

type InventoryDbRow = {
  item_key: string;
  sort_order: number;
  area: InventoryArea;
  item_name: string;
  size_label: string;
  unit: InventoryUnit;
  pack_quantity: number | string;
  current_stock: number | string;
  stock_saved_at: string | null;
  three_day_use: number | string;
  reorder_days: number;
  order_quantity: number | string | null;
};

type InventoryPageProps = {
  storageMode?: InventoryStorageMode;
  embedded?: boolean;
};

type InventoryDraft = Omit<InventoryItem, 'id' | 'sortOrder'>;

const STORAGE_KEY = 'satsuki.inventory.preview.v1';
const DAY_MS = 24 * 60 * 60 * 1000;

const areaOptions: InventoryArea[] = ['介護', '調理', '感染症'];
const unitOptions: InventoryUnit[] = ['箱', '巻', '個'];

const createEmptyDraft = (): InventoryDraft => ({
  area: '介護',
  itemName: '',
  size: '',
  unit: '箱',
  packQuantity: 100,
  currentStock: 0,
  stockSavedAt: new Date().toISOString(),
  threeDayUse: 0,
  reorderDays: 60,
  orderQuantity: 0
});

const createCustomItemId = () =>
  `custom-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const initialStockSavedAt = new Date().toISOString();

const initialItems: InventoryItem[] = [
  {
    id: 'care-dispo-s',
    sortOrder: 10,
    area: '介護',
    itemName: 'ディスポ手袋',
    size: 'S',
    unit: '箱',
    packQuantity: 100,
    currentStock: 550,
    stockSavedAt: initialStockSavedAt,
    threeDayUse: 1,
    reorderDays: 60,
    orderQuantity: 0
  },
  {
    id: 'care-dispo-m',
    sortOrder: 20,
    area: '介護',
    itemName: 'ディスポ手袋',
    size: 'M',
    unit: '箱',
    packQuantity: 100,
    currentStock: 300,
    stockSavedAt: initialStockSavedAt,
    threeDayUse: 3,
    reorderDays: 60,
    orderQuantity: 0
  },
  {
    id: 'care-dispo-l',
    sortOrder: 30,
    area: '介護',
    itemName: 'ディスポ手袋',
    size: 'L',
    unit: '箱',
    packQuantity: 100,
    currentStock: 330,
    stockSavedAt: initialStockSavedAt,
    threeDayUse: 2.5,
    reorderDays: 60,
    orderQuantity: 0
  },
  {
    id: 'kitchen-dispo-m',
    sortOrder: 40,
    area: '調理',
    itemName: 'ディスポ手袋',
    size: 'M',
    unit: '箱',
    packQuantity: 100,
    currentStock: 0,
    stockSavedAt: initialStockSavedAt,
    threeDayUse: 0,
    reorderDays: 60,
    orderQuantity: 0
  },
  {
    id: 'kitchen-dispo-l',
    sortOrder: 50,
    area: '調理',
    itemName: 'ディスポ手袋',
    size: 'L',
    unit: '箱',
    packQuantity: 100,
    currentStock: 0,
    stockSavedAt: initialStockSavedAt,
    threeDayUse: 0,
    reorderDays: 60,
    orderQuantity: 0
  },
  {
    id: 'kitchen-nitrile-m',
    sortOrder: 60,
    area: '調理',
    itemName: 'ニトリル手袋',
    size: 'M',
    unit: '箱',
    packQuantity: 100,
    currentStock: 0,
    stockSavedAt: initialStockSavedAt,
    threeDayUse: 0,
    reorderDays: 60,
    orderQuantity: 0
  },
  {
    id: 'kitchen-nitrile-l',
    sortOrder: 70,
    area: '調理',
    itemName: 'ニトリル手袋',
    size: 'L',
    unit: '箱',
    packQuantity: 100,
    currentStock: 0,
    stockSavedAt: initialStockSavedAt,
    threeDayUse: 0,
    reorderDays: 60,
    orderQuantity: 0
  },
  {
    id: 'infection-mask',
    sortOrder: 80,
    area: '感染症',
    itemName: '感染症グッズ',
    size: '一式',
    unit: '個',
    packQuantity: 1,
    currentStock: 0,
    stockSavedAt: initialStockSavedAt,
    threeDayUse: 0,
    reorderDays: 60,
    orderQuantity: 0
  },
  {
    id: 'kitchen-wrap',
    sortOrder: 90,
    area: '調理',
    itemName: 'サランラップ',
    size: '標準',
    unit: '巻',
    packQuantity: 1,
    currentStock: 0,
    stockSavedAt: initialStockSavedAt,
    threeDayUse: 0,
    reorderDays: 30,
    orderQuantity: 0
  }
];

const statusMeta: Record<InventoryStatus, { label: string; tone: string; dot: string }> = {
  order: {
    label: '要発注',
    tone: 'bg-red-50 text-red-700 border-red-200',
    dot: 'bg-red-500'
  },
  ok: {
    label: '在庫あり',
    tone: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    dot: 'bg-emerald-500'
  },
  over: {
    label: '購入不要',
    tone: 'bg-sky-50 text-sky-700 border-sky-200',
    dot: 'bg-sky-500'
  },
  unknown: {
    label: '要確認',
    tone: 'bg-gray-50 text-gray-700 border-gray-200',
    dot: 'bg-gray-400'
  }
};

const areaTone: Record<InventoryArea, string> = {
  介護: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  調理: 'bg-orange-50 text-orange-800 border-orange-200',
  感染症: 'bg-rose-50 text-rose-800 border-rose-200'
};

const formatNumber = (value: number, fractionDigits = 0) =>
  new Intl.NumberFormat('ja-JP', {
    maximumFractionDigits: fractionDigits,
    minimumFractionDigits: fractionDigits
  }).format(value);

const formatDateTime = (iso: string) => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '-';
  return new Intl.DateTimeFormat('ja-JP', {
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date);
};

const clampNumber = (value: number, fallback = 0) => {
  if (!Number.isFinite(value) || value < 0) return fallback;
  return value;
};

const getElapsedDays = (iso: string) => {
  const savedTime = new Date(iso).getTime();
  if (!Number.isFinite(savedTime)) return 0;
  return Math.max(0, Math.floor((Date.now() - savedTime) / DAY_MS));
};

const evaluateItem = (item: InventoryItem): EvaluatedInventoryItem => {
  const dailyUse = item.threeDayUse > 0 ? item.threeDayUse / 3 : 0;
  const elapsedDays = getElapsedDays(item.stockSavedAt);
  const estimatedStock = Math.max(0, item.currentStock - dailyUse * elapsedDays);

  if (dailyUse <= 0) {
    return {
      ...item,
      dailyUse,
      elapsedDays: 0,
      estimatedStock: item.currentStock,
      daysLeft: null,
      reorderPoint: null,
      status: 'unknown'
    };
  }

  const daysLeft = estimatedStock / dailyUse;
  const reorderPoint = Math.ceil(dailyUse * item.reorderDays);
  const status: InventoryStatus =
    daysLeft <= item.reorderDays
      ? 'order'
      : daysLeft >= Math.max(365, item.reorderDays * 4)
        ? 'over'
        : 'ok';

  return {
    ...item,
    dailyUse,
    elapsedDays,
    estimatedStock,
    daysLeft,
    reorderPoint,
    status
  };
};

const readStoredItems = (): InventoryItem[] => {
  if (typeof window === 'undefined') return initialItems;

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return initialItems;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return initialItems;
    return parsed.map((item, index) => {
      const base = initialItems[index] ?? createEmptyDraft();
      return {
        ...base,
        ...item,
        id: item.id ?? `local-${index}`,
        sortOrder: clampNumber(Number(item.sortOrder), (index + 1) * 10),
        currentStock: clampNumber(Number(item.currentStock)),
        stockSavedAt: item.stockSavedAt ?? initialStockSavedAt,
        threeDayUse: clampNumber(Number(item.threeDayUse)),
        reorderDays: clampNumber(Number(item.reorderDays), 60),
        orderQuantity: clampNumber(Number(item.orderQuantity))
      };
    });
  } catch {
    return initialItems;
  }
};

const mapDbRowToItem = (row: InventoryDbRow): InventoryItem => ({
  id: row.item_key,
  sortOrder: row.sort_order,
  area: row.area,
  itemName: row.item_name,
  size: row.size_label,
  unit: row.unit,
  packQuantity: clampNumber(Number(row.pack_quantity), 1),
  currentStock: clampNumber(Number(row.current_stock)),
  stockSavedAt: row.stock_saved_at ?? initialStockSavedAt,
  threeDayUse: clampNumber(Number(row.three_day_use)),
  reorderDays: clampNumber(Number(row.reorder_days), 60),
  orderQuantity: clampNumber(Number(row.order_quantity))
});

const itemToDbPayload = (item: InventoryItem) => ({
  item_key: item.id,
  sort_order: item.sortOrder ?? 0,
  area: item.area,
  item_name: item.itemName,
  size_label: item.size,
  unit: item.unit,
  pack_quantity: item.packQuantity,
  current_stock: item.currentStock,
  stock_saved_at: item.stockSavedAt,
  three_day_use: item.threeDayUse,
  reorder_days: item.reorderDays,
  order_quantity: item.orderQuantity,
  active: true
});

const fieldToDbColumn: Record<
  keyof Pick<InventoryItem, 'threeDayUse' | 'reorderDays' | 'orderQuantity'>,
  string
> = {
  threeDayUse: 'three_day_use',
  reorderDays: 'reorder_days',
  orderQuantity: 'order_quantity'
};

const InventoryPage = ({ storageMode = 'local', embedded = false }: InventoryPageProps) => {
  const isSupabaseMode = storageMode === 'supabase';
  const [items, setItems] = useState<InventoryItem[]>(() => (isSupabaseMode ? initialItems : readStoredItems()));
  const [statusFilter, setStatusFilter] = useState<'all' | InventoryStatus>('all');
  const [areaFilter, setAreaFilter] = useState<'all' | InventoryArea>('all');
  const [query, setQuery] = useState('');
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(isSupabaseMode);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [draft, setDraft] = useState<InventoryDraft>(createEmptyDraft);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const previousTitle = document.title;
    document.title = '消耗品在庫管理 | さつきホーム';
    return () => {
      document.title = previousTitle;
    };
  }, []);

  const loadInventoryFromSupabase = useCallback(async () => {
    if (!supabaseClient) {
      setLoadError('Supabase接続設定が見つかりません。');
      setLoading(false);
      return;
    }

    setLoading(true);
    setLoadError(null);

    const { data, error } = await supabaseClient
      .from('inventory_items')
      .select('item_key, sort_order, area, item_name, size_label, unit, pack_quantity, current_stock, stock_saved_at, three_day_use, reorder_days, order_quantity')
      .eq('active', true)
      .order('sort_order', { ascending: true });

    if (error) {
      setLoadError(`在庫データの取得に失敗しました: ${error.message}`);
      setLoading(false);
      return;
    }

    const rows = (data ?? []) as InventoryDbRow[];
    setItems(rows.length > 0 ? rows.map(mapDbRowToItem) : initialItems);
    setSaveStatus('idle');
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!isSupabaseMode) return;
    void loadInventoryFromSupabase();
  }, [isSupabaseMode, loadInventoryFromSupabase]);

  useEffect(() => {
    if (isSupabaseMode) return;
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [isSupabaseMode, items]);

  const evaluatedItems = useMemo(() => items.map(evaluateItem), [items]);

  const filteredItems = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return evaluatedItems.filter(item => {
      const matchesStatus = statusFilter === 'all' || item.status === statusFilter;
      const matchesArea = areaFilter === 'all' || item.area === areaFilter;
      const matchesQuery =
        normalizedQuery.length === 0 ||
        `${item.area} ${item.itemName} ${item.size}`.toLowerCase().includes(normalizedQuery);
      return matchesStatus && matchesArea && matchesQuery;
    });
  }, [areaFilter, evaluatedItems, query, statusFilter]);

  const orderCandidates = useMemo(
    () =>
      evaluatedItems
        .filter(item => item.status === 'order')
        .sort((a, b) => {
          const aDays = a.daysLeft ?? Number.POSITIVE_INFINITY;
          const bDays = b.daysLeft ?? Number.POSITIVE_INFINITY;
          return aDays - bDays;
        }),
    [evaluatedItems]
  );

  const summary = useMemo(() => {
    const knownItems = evaluatedItems.filter(item => item.daysLeft !== null);
    const minDays = knownItems.length
      ? Math.min(...knownItems.map(item => item.daysLeft ?? Number.POSITIVE_INFINITY))
      : null;

    return {
      order: evaluatedItems.filter(item => item.status === 'order').length,
      ok: evaluatedItems.filter(item => item.status === 'ok').length,
      over: evaluatedItems.filter(item => item.status === 'over').length,
      unknown: evaluatedItems.filter(item => item.status === 'unknown').length,
      minDays
    };
  }, [evaluatedItems]);

  const persistItemField = useCallback(
    async (
      id: string,
      field: keyof Pick<InventoryItem, 'threeDayUse' | 'reorderDays' | 'orderQuantity'>,
      value: number
    ) => {
      if (!isSupabaseMode) return;

      if (!supabaseClient) {
        setSaveStatus('error');
        setLoadError('Supabase接続設定が見つかりません。');
        return;
      }

      setSaveStatus('saving');
      const { error } = await supabaseClient
        .from('inventory_items')
        .update({ [fieldToDbColumn[field]]: value })
        .eq('item_key', id);

      if (error) {
        setSaveStatus('error');
        setLoadError(`在庫データの保存に失敗しました: ${error.message}`);
        return;
      }

      setLoadError(null);
      setSaveStatus('saved');
      window.setTimeout(() => setSaveStatus('idle'), 1600);
    },
    [isSupabaseMode]
  );

  const updateItem = useCallback(
    (id: string, field: keyof Pick<InventoryItem, 'threeDayUse' | 'reorderDays' | 'orderQuantity'>, value: string) => {
      const nextValue = clampNumber(Number(value));
      setItems(prev =>
        prev.map(item =>
          item.id === id
            ? {
                ...item,
                [field]: nextValue
              }
            : item
        )
      );
      void persistItemField(id, field, nextValue);
    },
    [persistItemField]
  );

  const saveStockBaseline = useCallback(
    async (id: string, value: string) => {
      const nextValue = clampNumber(Number(value));
      const savedAt = new Date().toISOString();
      const previousItems = items;

      setItems(prev =>
        prev.map(item =>
          item.id === id
            ? {
                ...item,
                currentStock: nextValue,
                stockSavedAt: savedAt
              }
            : item
        )
      );

      if (!isSupabaseMode) return;

      if (!supabaseClient) {
        setItems(previousItems);
        setSaveStatus('error');
        setLoadError('Supabase接続設定が見つかりません。');
        return;
      }

      setSaveStatus('saving');
      const { error } = await supabaseClient
        .from('inventory_items')
        .update({
          current_stock: nextValue,
          stock_saved_at: savedAt
        })
        .eq('item_key', id);

      if (error) {
        setItems(previousItems);
        setSaveStatus('error');
        setLoadError(`在庫数の保存に失敗しました: ${error.message}`);
        return;
      }

      setLoadError(null);
      setSaveStatus('saved');
      window.setTimeout(() => setSaveStatus('idle'), 1600);
    },
    [isSupabaseMode, items]
  );

  const addItem = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();
      const itemName = draft.itemName.trim();
      const size = draft.size.trim();

      if (!itemName) {
        setFormError('品目名を入力してください。');
        return;
      }

      const maxSortOrder = items.reduce((max, item) => Math.max(max, item.sortOrder ?? 0), 0);
      const nextItem: InventoryItem = {
        ...draft,
        id: createCustomItemId(),
        sortOrder: maxSortOrder + 10,
        itemName,
        size: size || '-',
        packQuantity: clampNumber(Number(draft.packQuantity), 1),
        currentStock: clampNumber(Number(draft.currentStock)),
        stockSavedAt: new Date().toISOString(),
        threeDayUse: clampNumber(Number(draft.threeDayUse)),
        reorderDays: clampNumber(Number(draft.reorderDays), 60),
        orderQuantity: clampNumber(Number(draft.orderQuantity))
      };

      setFormError(null);
      setItems(prev => [...prev, nextItem]);

      if (isSupabaseMode) {
        if (!supabaseClient) {
          setItems(prev => prev.filter(item => item.id !== nextItem.id));
          setSaveStatus('error');
          setFormError('Supabase接続設定が見つかりません。');
          return;
        }

        setSaveStatus('saving');
        const { error } = await supabaseClient
          .from('inventory_items')
          .insert(itemToDbPayload(nextItem));

        if (error) {
          setItems(prev => prev.filter(item => item.id !== nextItem.id));
          setSaveStatus('error');
          setFormError(`品目の追加に失敗しました: ${error.message}`);
          return;
        }

        setSaveStatus('saved');
        window.setTimeout(() => setSaveStatus('idle'), 1600);
      }

      setDraft(createEmptyDraft());
      setIsAddOpen(false);
    },
    [draft, isSupabaseMode, items]
  );

  const deleteItem = useCallback(
    async (item: InventoryItem) => {
      const ok = window.confirm(`${item.itemName} ${item.size} を一覧から削除しますか？`);
      if (!ok) return;

      setItems(prev => prev.filter(current => current.id !== item.id));

      if (isSupabaseMode) {
        if (!supabaseClient) {
          setSaveStatus('error');
          setLoadError('Supabase接続設定が見つかりません。');
          return;
        }

        setSaveStatus('saving');
        const { error } = await supabaseClient
          .rpc('archive_inventory_item', { _item_key: item.id });

        if (error) {
          setItems(prev => [...prev, item].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)));
          setSaveStatus('error');
          setLoadError(`品目の削除に失敗しました: ${error.message}`);
          return;
        }

        setSaveStatus('saved');
        window.setTimeout(() => setSaveStatus('idle'), 1600);
      }
    },
    [isSupabaseMode]
  );

  const resetItems = () => {
    setItems(initialItems);
    setStatusFilter('all');
    setAreaFilter('all');
    setQuery('');
  };

  const handleReloadOrReset = () => {
    if (isSupabaseMode) {
      void loadInventoryFromSupabase();
      return;
    }

    resetItems();
  };

  const buildOrderText = () => {
    const lines = orderCandidates.map(item => {
      const daysLabel = item.daysLeft === null ? '要確認' : `残${formatNumber(item.daysLeft, 0)}日`;
      const amountLabel = item.orderQuantity > 0
        ? `${formatNumber(item.orderQuantity, 0)}${item.unit}`
        : '発注数確認';
      return `・${item.area} ${item.itemName}${item.size}: ${amountLabel}（${daysLabel} / 発注ライン${formatNumber(item.reorderDays, 0)}日）`;
    });

    if (lines.length === 0) {
      return '現在、発注候補はありません。';
    }

    return ['さつきホーム 在庫発注候補', ...lines].join('\n');
  };

  const copyOrderList = async () => {
    const text = buildOrderText();
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      setCopied(false);
    }
  };

  const downloadCsv = () => {
    const header = [
      '用途',
      '品目',
      'サイズ',
      '推定現在庫',
      '保存時在庫',
      '在庫保存日時',
      '単位',
      '3日消費',
      '1日消費',
      '残日数',
      '発注ライン日数',
      '発注ライン在庫数',
      '1回の発注数',
      '判定'
    ];
    const rows = evaluatedItems.map(item => [
      item.area,
      item.itemName,
      item.size,
      item.dailyUse > 0 ? item.estimatedStock.toFixed(2) : String(item.currentStock),
      String(item.currentStock),
      formatDateTime(item.stockSavedAt),
      item.unit,
      String(item.threeDayUse),
      item.dailyUse > 0 ? item.dailyUse.toFixed(2) : '',
      item.daysLeft === null ? '' : item.daysLeft.toFixed(0),
      String(item.reorderDays),
      item.reorderPoint === null ? '' : String(item.reorderPoint),
      item.orderQuantity > 0 ? String(item.orderQuantity) : '',
      statusMeta[item.status].label
    ]);
    const csv = [header, ...rows]
      .map(row => row.map(cell => `"${cell.replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = isSupabaseMode ? 'satsuki-inventory.csv' : 'satsuki-inventory-preview.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <main className={`${embedded ? 'min-h-0' : 'min-h-screen'} bg-[#f6f7f4] text-gray-900`}>
      <section className="border-b border-[#d7ded4] bg-[#20342f] text-white">
        <div className="mx-auto max-w-[1560px] px-4 py-5 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <div className="mb-2 flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-semibold text-white">
                  さつきホーム
                </span>
                <span className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold ${
                  isSupabaseMode
                    ? 'border-emerald-300/40 bg-emerald-300/15 text-emerald-100'
                    : 'border-amber-300/40 bg-amber-300/15 text-amber-100'
                }`}>
                  {isSupabaseMode ? 'DB保存' : '試作画面'}
                </span>
                {isSupabaseMode && (
                  <span className="inline-flex items-center rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-semibold text-white">
                    {saveStatus === 'saving' && '保存中'}
                    {saveStatus === 'saved' && '保存済み'}
                    {saveStatus === 'error' && '保存エラー'}
                    {saveStatus === 'idle' && '本番データ'}
                  </span>
                )}
                {loading && (
                  <span className="inline-flex items-center rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-semibold text-white">
                    読み込み中
                  </span>
                )}
              </div>
              <h1 className="text-2xl font-bold tracking-normal sm:text-3xl">消耗品在庫管理</h1>
              {loadError && (
                <p className="mt-2 max-w-3xl text-sm font-medium text-red-100">
                  {loadError}
                </p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              <SummaryTile label="要発注" value={`${summary.order}件`} tone="bg-red-500/15 text-red-50 border-red-300/30" />
              <SummaryTile label="在庫あり" value={`${summary.ok}件`} tone="bg-emerald-400/15 text-emerald-50 border-emerald-300/30" />
              <SummaryTile label="購入不要" value={`${summary.over}件`} tone="bg-sky-400/15 text-sky-50 border-sky-300/30" />
              <SummaryTile
                label="最短残日数"
                value={summary.minDays === null ? '-' : `${formatNumber(summary.minDays, 0)}日`}
                tone="bg-white/10 text-white border-white/20"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="border-b border-[#d7ded4] bg-white">
        <div className="mx-auto flex max-w-[1560px] flex-col gap-3 px-4 py-4 sm:px-6 lg:flex-row lg:items-center lg:justify-between lg:px-8">
          <div className="flex flex-col gap-3 md:flex-row md:items-center">
            <Link to="/care" className="inline-flex h-11 items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 text-sm font-semibold text-gray-700 transition hover:bg-gray-50">
              <ArrowLeft className="h-4 w-4" />
              ケアトップ
            </Link>
            <div className="relative w-full md:w-72">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                value={query}
                onChange={event => setQuery(event.target.value)}
                className="h-11 w-full rounded-lg border border-gray-300 bg-white pl-9 pr-3 text-sm outline-none transition focus:border-emerald-600 focus:ring-2 focus:ring-emerald-100"
                placeholder="品目・サイズで検索"
              />
            </div>

            <select
              value={areaFilter}
              onChange={event => setAreaFilter(event.target.value as 'all' | InventoryArea)}
              className="h-11 rounded-lg border border-gray-300 bg-white px-3 text-sm outline-none transition focus:border-emerald-600 focus:ring-2 focus:ring-emerald-100"
            >
              <option value="all">全用途</option>
              <option value="介護">介護</option>
              <option value="調理">調理</option>
              <option value="感染症">感染症</option>
            </select>

            <select
              value={statusFilter}
              onChange={event => setStatusFilter(event.target.value as 'all' | InventoryStatus)}
              className="h-11 rounded-lg border border-gray-300 bg-white px-3 text-sm outline-none transition focus:border-emerald-600 focus:ring-2 focus:ring-emerald-100"
            >
              <option value="all">全判定</option>
              <option value="order">要発注</option>
              <option value="ok">在庫あり</option>
              <option value="over">購入不要</option>
              <option value="unknown">要確認</option>
            </select>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => {
                setFormError(null);
                setIsAddOpen(true);
              }}
              className="inline-flex h-11 items-center gap-2 rounded-lg border border-emerald-700 bg-emerald-700 px-3 text-sm font-semibold text-white transition hover:bg-emerald-800"
            >
              <Plus className="h-4 w-4" />
              品目追加
            </button>
            <button
              onClick={copyOrderList}
              className="inline-flex h-11 items-center gap-2 rounded-lg border border-[#20342f] bg-[#20342f] px-3 text-sm font-semibold text-white transition hover:bg-[#2b4740]"
            >
              <ClipboardList className="h-4 w-4" />
              {copied ? 'コピー済み' : '発注メモ'}
            </button>
            <button
              onClick={downloadCsv}
              className="inline-flex h-11 items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 text-sm font-semibold text-gray-700 transition hover:bg-gray-50"
            >
              <Download className="h-4 w-4" />
              CSV
            </button>
            <button
              onClick={handleReloadOrReset}
              className="inline-flex h-11 items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 text-sm font-semibold text-gray-700 transition hover:bg-gray-50"
            >
              {isSupabaseMode ? <RefreshCw className="h-4 w-4" /> : <RotateCcw className="h-4 w-4" />}
              {isSupabaseMode ? '再読込' : '初期値'}
            </button>
          </div>
        </div>
      </section>

      <div className="mx-auto grid max-w-[1560px] gap-5 px-4 py-5 sm:px-6 lg:px-8 2xl:grid-cols-[minmax(0,1fr)_360px]">
        <section className="overflow-hidden rounded-lg border border-[#d7ded4] bg-white shadow-sm">
          <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
            <div className="flex items-center gap-2">
              <Boxes className="h-5 w-5 text-emerald-700" />
              <h2 className="text-base font-bold text-gray-900">在庫一覧</h2>
            </div>
            <span className="text-sm text-gray-500">{filteredItems.length}件</span>
          </div>

          <div className="max-h-[72vh] overflow-auto overscroll-contain">
            <table className="min-w-[1420px] w-full table-fixed border-collapse text-sm">
              <thead className="sticky top-0 z-30 bg-[#f0f3ee] text-xs font-semibold text-gray-600 shadow-[0_1px_0_#d7ded4]">
                <tr>
                  <th className="w-[220px] px-4 py-3 text-left">品目</th>
                  <th className="w-[96px] px-3 py-3 text-left">用途</th>
                  <th className="w-[150px] px-3 py-3 text-right">現在庫（推定）</th>
                  <th className="w-[156px] px-3 py-3 text-right">実在庫更新</th>
                  <th className="w-[112px] px-3 py-3 text-right">3日消費</th>
                  <th className="w-[92px] px-3 py-3 text-right">1日消費</th>
                  <th className="w-[120px] px-3 py-3 text-right">残日数</th>
                  <th className="w-[170px] px-3 py-3 text-left">発注ライン</th>
                  <th className="w-[132px] px-3 py-3 text-right">1回の発注数</th>
                  <th className="w-[116px] px-3 py-3 text-left">判定</th>
                  <th className="w-[72px] px-3 py-3 text-center">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredItems.map(item => (
                  <InventoryRow key={item.id} item={item} onUpdate={updateItem} onSaveStock={saveStockBaseline} onDelete={deleteItem} />
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <aside className="space-y-5">
          <section className="rounded-lg border border-[#d7ded4] bg-white shadow-sm">
            <div className="flex items-center gap-2 border-b border-gray-200 px-4 py-3">
              <ShoppingCart className="h-5 w-5 text-amber-700" />
              <h2 className="text-base font-bold text-gray-900">発注一覧</h2>
            </div>
            <div className="divide-y divide-gray-100">
              {orderCandidates.length === 0 && (
                <div className="p-4 text-sm text-gray-500">発注候補はありません。</div>
              )}
              {orderCandidates.map(item => (
                <div key={item.id} className="p-4">
                  <div className="mb-2 flex items-start justify-between gap-3">
                    <div>
                      <p className="font-bold text-gray-900">
                        {item.itemName} {item.size}
                      </p>
                      <p className="mt-1 text-xs text-gray-500">{item.area}</p>
                    </div>
                    <StatusBadge status={item.status} />
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <Metric label="残日数" value={item.daysLeft === null ? '-' : `${formatNumber(item.daysLeft, 0)}日`} />
                    <Metric
                      label="1回の発注数"
                      value={item.orderQuantity > 0 ? `${formatNumber(item.orderQuantity, 0)}${item.unit}` : '確認'}
                    />
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="rounded-lg border border-[#d7ded4] bg-white shadow-sm">
            <div className="flex items-center gap-2 border-b border-gray-200 px-4 py-3">
              <ShieldCheck className="h-5 w-5 text-emerald-700" />
              <h2 className="text-base font-bold text-gray-900">判定基準</h2>
            </div>
            <div className="space-y-3 p-4 text-sm">
              <RuleLine status="order" text="残日数が発注ライン以下" />
              <RuleLine status="ok" text="発注ラインより余裕あり" />
              <RuleLine status="over" text="当面購入不要の在庫量" />
              <RuleLine status="unknown" text="消費数が未入力" />
            </div>
          </section>

          <section className="rounded-lg border border-amber-200 bg-amber-50 shadow-sm">
            <div className="flex items-start gap-3 p-4">
              <AlertTriangle className="mt-0.5 h-5 w-5 flex-none text-amber-700" />
              <div className="text-sm text-amber-900">
                <p className="font-bold">在庫数は保存日時から自動計算</p>
                <p className="mt-1 leading-6">
                  実在庫を入力して保存すると、その日時を基準に1日消費数分ずつ推定現在庫を減らして表示します。
                </p>
              </div>
            </div>
          </section>
        </aside>
      </div>

      {isAddOpen && (
        <AddItemDialog
          draft={draft}
          error={formError}
          onChange={setDraft}
          onClose={() => {
            setFormError(null);
            setIsAddOpen(false);
          }}
          onSubmit={addItem}
        />
      )}
    </main>
  );
};

const SummaryTile = ({ label, value, tone }: { label: string; value: string; tone: string }) => (
  <div className={`min-w-[132px] rounded-lg border px-3 py-2 ${tone}`}>
    <p className="text-xs font-semibold opacity-80">{label}</p>
    <p className="mt-1 text-xl font-bold leading-none">{value}</p>
  </div>
);

const StatusBadge = ({ status }: { status: InventoryStatus }) => {
  const meta = statusMeta[status];
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-bold ${meta.tone}`}>
      <span className={`h-2 w-2 rounded-full ${meta.dot}`} />
      {meta.label}
    </span>
  );
};

const Metric = ({ label, value }: { label: string; value: string }) => (
  <div className="rounded-md bg-gray-50 px-3 py-2">
    <p className="text-xs text-gray-500">{label}</p>
    <p className="mt-1 font-bold text-gray-900">{value}</p>
  </div>
);

const RuleLine = ({ status, text }: { status: InventoryStatus; text: string }) => (
  <div className="flex items-center justify-between gap-3">
    <StatusBadge status={status} />
    <span className="text-right text-gray-600">{text}</span>
  </div>
);

type InventoryRowProps = {
  item: EvaluatedInventoryItem;
  onUpdate: (
    id: string,
    field: keyof Pick<InventoryItem, 'threeDayUse' | 'reorderDays' | 'orderQuantity'>,
    value: string
  ) => void;
  onSaveStock: (id: string, value: string) => void;
  onDelete: (item: InventoryItem) => void;
};

const InventoryRow = ({ item, onUpdate, onSaveStock, onDelete }: InventoryRowProps) => {
  const [stockDraft, setStockDraft] = useState(String(item.currentStock));

  useEffect(() => {
    setStockDraft(String(item.currentStock));
  }, [item.currentStock, item.stockSavedAt]);

  const coverage = item.daysLeft === null
    ? 0
    : Math.min(100, Math.round((item.daysLeft / Math.max(item.reorderDays * 4, 1)) * 100));
  const barColor =
    item.status === 'order'
      ? 'bg-red-500'
      : item.status === 'over'
        ? 'bg-sky-500'
        : item.status === 'unknown'
          ? 'bg-gray-300'
          : 'bg-emerald-600';

  return (
    <tr className="align-top transition hover:bg-[#fbfcfa]">
      <td className="px-4 py-3">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-[#edf4ef] text-emerald-800">
            {item.status === 'over' ? <PackageCheck className="h-5 w-5" /> : <PackagePlus className="h-5 w-5" />}
          </div>
          <div>
            <p className="font-bold text-gray-900">
              {item.itemName} {item.size}
            </p>
            <p className="mt-1 text-xs text-gray-500">
              1{item.unit}{item.packQuantity > 1 ? `${item.packQuantity}枚入` : ''}
            </p>
          </div>
        </div>
      </td>
      <td className="px-3 py-3">
        <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-bold ${areaTone[item.area]}`}>
          {item.area}
        </span>
      </td>
      <td className="px-3 py-3 text-right">
        <p className="text-xl font-bold text-gray-900">
          {item.daysLeft === null ? formatNumber(item.currentStock, 0) : formatNumber(item.estimatedStock, 0)}
          <span className="ml-1 text-xs font-semibold text-gray-500">{item.unit}</span>
        </p>
        <p className="mt-1 text-xs text-gray-500">基準 {formatNumber(item.currentStock, 0)}{item.unit}</p>
        <p className="mt-0.5 inline-flex items-center justify-end gap-1 text-xs text-gray-500">
          <CalendarClock className="h-3 w-3" />
          {formatDateTime(item.stockSavedAt)}
        </p>
        {item.elapsedDays > 0 && (
          <p className="mt-0.5 text-xs text-gray-500">{item.elapsedDays}日経過</p>
        )}
      </td>
      <td className="px-3 py-3 text-right">
        <input
          aria-label={`${item.itemName}${item.size} 実在庫`}
          type="number"
          min="0"
          step="1"
          value={stockDraft}
          onChange={event => setStockDraft(event.target.value)}
          className="h-10 w-24 rounded-lg border border-gray-300 bg-white px-2 text-right font-semibold text-gray-900 outline-none transition focus:border-emerald-600 focus:ring-2 focus:ring-emerald-100"
        />
        <button
          type="button"
          onClick={() => onSaveStock(item.id, stockDraft)}
          className="mt-2 inline-flex h-8 items-center justify-center gap-1 rounded-md border border-emerald-700 bg-emerald-700 px-2 text-xs font-bold text-white transition hover:bg-emerald-800"
        >
          <Save className="h-3.5 w-3.5" />
          保存
        </button>
      </td>
      <td className="px-3 py-3 text-right">
        <NumberInput
          value={item.threeDayUse}
          step="0.5"
          label={`${item.itemName}${item.size} 3日消費`}
          onChange={value => onUpdate(item.id, 'threeDayUse', value)}
        />
        <p className="mt-1 text-xs text-gray-500">{item.unit}</p>
      </td>
      <td className="px-3 py-3 text-right font-semibold text-gray-700">
        {item.dailyUse > 0 ? `${formatNumber(item.dailyUse, 2)}${item.unit}` : '-'}
      </td>
      <td className="px-3 py-3 text-right">
        <p className="text-lg font-bold text-gray-900">
          {item.daysLeft === null ? '-' : formatNumber(item.daysLeft, 0)}
          {item.daysLeft !== null && <span className="ml-1 text-xs font-semibold text-gray-500">日</span>}
        </p>
      </td>
      <td className="px-3 py-3">
        <div className="mb-2 h-2 rounded-full bg-gray-100">
          <div className={`h-2 rounded-full ${barColor}`} style={{ width: `${coverage}%` }} />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <SmallNumberInput
            value={item.reorderDays}
            label="発注ライン"
            suffix="日"
            onChange={value => onUpdate(item.id, 'reorderDays', value)}
          />
          <div className="rounded-md bg-gray-50 px-2 py-1.5 text-right">
            <p className="text-[11px] font-semibold text-gray-500">ライン在庫</p>
            <p className="mt-1 text-sm font-bold text-gray-900">
              {item.reorderPoint === null ? '-' : formatNumber(item.reorderPoint, 0)}
              {item.reorderPoint !== null && <span className="ml-0.5 text-xs text-gray-500">{item.unit}</span>}
            </p>
          </div>
        </div>
      </td>
      <td className="px-3 py-3 text-right">
        <SmallNumberInput
          value={item.orderQuantity}
          label="1回"
          suffix={item.unit}
          onChange={value => onUpdate(item.id, 'orderQuantity', value)}
        />
      </td>
      <td className="px-3 py-3">
        <StatusBadge status={item.status} />
      </td>
      <td className="px-3 py-3 text-center">
        <button
          type="button"
          onClick={() => onDelete(item)}
          className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-red-100 bg-red-50 text-red-600 transition hover:border-red-200 hover:bg-red-100"
          aria-label={`${item.itemName}${item.size}を削除`}
          title="削除"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </td>
    </tr>
  );
};

const NumberInput = ({
  value,
  step,
  label,
  onChange
}: {
  value: number;
  step: string;
  label: string;
  onChange: (value: string) => void;
}) => (
  <input
    aria-label={label}
    type="number"
    min="0"
    step={step}
    value={value}
    onChange={event => onChange(event.target.value)}
    className="h-10 w-24 rounded-lg border border-gray-300 bg-white px-2 text-right font-semibold text-gray-900 outline-none transition focus:border-emerald-600 focus:ring-2 focus:ring-emerald-100"
  />
);

const SmallNumberInput = ({
  value,
  label,
  suffix,
  onChange
}: {
  value: number;
  label: string;
  suffix: string;
  onChange: (value: string) => void;
}) => (
  <label className="block min-w-[78px]">
    <span className="mb-1 block text-[11px] font-semibold text-gray-500">{label}</span>
    <span className="flex h-9 items-center rounded-md border border-gray-300 bg-white pr-2">
      <input
        type="number"
        min="0"
        step="1"
        value={value}
        onChange={event => onChange(event.target.value)}
        className="w-12 flex-none bg-transparent px-2 text-right text-sm font-semibold outline-none"
      />
      <span className="text-xs text-gray-500">{suffix}</span>
    </span>
  </label>
);

const AddItemDialog = ({
  draft,
  error,
  onChange,
  onClose,
  onSubmit
}: {
  draft: InventoryDraft;
  error: string | null;
  onChange: (draft: InventoryDraft) => void;
  onClose: () => void;
  onSubmit: (event: FormEvent) => void;
}) => {
  const updateDraft = (patch: Partial<InventoryDraft>) => {
    onChange({ ...draft, ...patch });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 px-4 py-6">
      <div className="w-full max-w-2xl overflow-hidden rounded-lg bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
          <div>
            <h2 className="text-lg font-bold text-gray-900">品目追加</h2>
            <p className="mt-1 text-sm text-gray-500">新しい消耗品を在庫一覧に追加します。</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-gray-500 transition hover:bg-gray-100 hover:text-gray-700"
            aria-label="閉じる"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={onSubmit} className="p-5">
          {error && (
            <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
              {error}
            </div>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block">
              <span className="mb-1.5 block text-sm font-semibold text-gray-700">用途</span>
              <select
                value={draft.area}
                onChange={event => updateDraft({ area: event.target.value as InventoryArea })}
                className="h-11 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm outline-none transition focus:border-emerald-600 focus:ring-2 focus:ring-emerald-100"
              >
                {areaOptions.map(area => (
                  <option key={area} value={area}>{area}</option>
                ))}
              </select>
            </label>

            <label className="block">
              <span className="mb-1.5 block text-sm font-semibold text-gray-700">単位</span>
              <select
                value={draft.unit}
                onChange={event => updateDraft({ unit: event.target.value as InventoryUnit })}
                className="h-11 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm outline-none transition focus:border-emerald-600 focus:ring-2 focus:ring-emerald-100"
              >
                {unitOptions.map(unit => (
                  <option key={unit} value={unit}>{unit}</option>
                ))}
              </select>
            </label>

            <TextField
              label="品目名"
              value={draft.itemName}
              placeholder="例: ペーパータオル"
              onChange={value => updateDraft({ itemName: value })}
            />

            <TextField
              label="サイズ・規格"
              value={draft.size}
              placeholder="例: M / 200枚 / 標準"
              onChange={value => updateDraft({ size: value })}
            />

            <DialogNumberField
              label={`1${draft.unit}あたりの入数`}
              value={draft.packQuantity}
              step="1"
              onChange={value => updateDraft({ packQuantity: value })}
            />

            <DialogNumberField
              label="実在庫（保存時点）"
              value={draft.currentStock}
              step="1"
              onChange={value => updateDraft({ currentStock: value })}
            />

            <DialogNumberField
              label="3日消費"
              value={draft.threeDayUse}
              step="0.5"
              onChange={value => updateDraft({ threeDayUse: value })}
            />

            <DialogNumberField
              label="発注ライン（日数）"
              value={draft.reorderDays}
              step="1"
              onChange={value => updateDraft({ reorderDays: value })}
            />

            <DialogNumberField
              label={`1回の発注数（${draft.unit}）`}
              value={draft.orderQuantity}
              step="1"
              onChange={value => updateDraft({ orderQuantity: value })}
            />
          </div>

          <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-11 items-center justify-center rounded-lg border border-gray-300 bg-white px-4 text-sm font-semibold text-gray-700 transition hover:bg-gray-50"
            >
              キャンセル
            </button>
            <button
              type="submit"
              className="inline-flex h-11 items-center justify-center gap-2 rounded-lg border border-emerald-700 bg-emerald-700 px-4 text-sm font-semibold text-white transition hover:bg-emerald-800"
            >
              <Plus className="h-4 w-4" />
              追加する
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const TextField = ({
  label,
  value,
  placeholder,
  onChange
}: {
  label: string;
  value: string;
  placeholder: string;
  onChange: (value: string) => void;
}) => (
  <label className="block">
    <span className="mb-1.5 block text-sm font-semibold text-gray-700">{label}</span>
    <input
      value={value}
      placeholder={placeholder}
      onChange={event => onChange(event.target.value)}
      className="h-11 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm outline-none transition focus:border-emerald-600 focus:ring-2 focus:ring-emerald-100"
    />
  </label>
);

const DialogNumberField = ({
  label,
  value,
  step,
  onChange
}: {
  label: string;
  value: number;
  step: string;
  onChange: (value: number) => void;
}) => (
  <label className="block">
    <span className="mb-1.5 block text-sm font-semibold text-gray-700">{label}</span>
    <input
      type="number"
      min="0"
      step={step}
      value={value}
      onChange={event => onChange(clampNumber(Number(event.target.value)))}
      className="h-11 w-full rounded-lg border border-gray-300 bg-white px-3 text-right text-sm font-semibold outline-none transition focus:border-emerald-600 focus:ring-2 focus:ring-emerald-100"
    />
  </label>
);

export default InventoryPage;
